import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getListOrganizationCategoryFoldersQueryKey,
  useCreateOrganizationCategoryFolder,
} from "~/api/organization/organization";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";

/**
 * Default color assigned to newly created folders.
 * This is the first color from the palette (slate).
 * Users can change the folder color after creation by clicking the color circle in FolderListItem.
 */
const DEFAULT_FOLDER_COLOR = "#64748b";

/**
 * CreateFolderModal allows users to create a new folder for organizing categories.
 *
 * Features:
 * - Create folder with name (required) and description (optional)
 * - Form validation with error toasts
 * - Form resets on successful creation or cancel
 *
 * Server State Management:
 * - Uses React Query's useCreateOrganizationCategoryFolder mutation to create folder
 * - Invalidates getListOrganizationCategoryFoldersQueryKey after successful creation
 *
 * UX Notes:
 * - Color is NOT selectable in this modal; new folders get DEFAULT_FOLDER_COLOR
 * - Users can change folder color after creation via the color circle in FolderListItem
 * - Modal closes and form resets automatically on successful creation
 */
interface CreateFolderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateFolderModal({
  open,
  onOpenChange,
}: CreateFolderModalProps) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: createFolder, isPending } =
    useCreateOrganizationCategoryFolder();
  const { toast } = useToast();
  const intl = useIntl();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "folder.nameRequired" }),
      });
      return;
    }

    try {
      await createFolder({
        organizationId: organizationId,
        data: {
          name: formData.name,
          description: formData.description || undefined,
          color: DEFAULT_FOLDER_COLOR,
        },
      });

      queryClient.invalidateQueries({
        queryKey: getListOrganizationCategoryFoldersQueryKey(organizationId),
      });

      onOpenChange(false);
      setFormData({ name: "", description: "" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "folder.createError" }),
      });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setFormData({ name: "", description: "" });
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={intl.formatMessage({ id: "folder.create.title" })}
      description={intl.formatMessage({ id: "folder.create.description" })}
      content={
        <form onSubmit={handleSubmit} id="create-folder-form">
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="folder-name">
                {intl.formatMessage({ id: "folder.name" })}
              </Label>
              <Input
                id="folder-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "folder.namePlaceholder",
                })}
                className="shadow-none"
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="folder-description">
                {intl.formatMessage({ id: "folder.description" })}
              </Label>
              <AutoResizeTextarea
                id="folder-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "folder.descriptionPlaceholder",
                })}
              />
            </div>
          </div>
        </form>
      }
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isPending}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button type="submit" form="create-folder-form" disabled={isPending}>
            <FormattedMessage id="create" />
          </Button>
        </>
      }
    />
  );
}
